(function() {
'use strict';

angular
    .module('TR')
    .service('dateUtil', dateUtil);

function dateUtil($injector,
                  $filter,
                  $route,
                  resourceService) {
    const oneDay = 24 * 60 * 60 * 1000;

    const service = {
        addDays,
        addMinutes,
        addHours,
        atLeastOneDayInTheFuture,
        atLeastTwoDaysInTheFuture,
        DateRange,
        format,
        getAge,
        getDurationSummary,
        isValid,
        minutesToHoursAndMinutes,
        numberOfNights,
        parseDate,
        serialiseDate,
        alignDates,
        sameDay,
        sameMonth,
        todayPlus,
        defaultTime,
        zeroTime,
        roundedToStartOfHour,
        dayDiff,
        thisWeek,
        nextWeek,
        thisMonth,
        specificMonth,
        oneDay: () => oneDay,
        compareDates,
        totalMinutes,
        getWeeksString,
        getDifferenceInDays,
        getDateInterval,
        checkZonedDateFromat,
        getNextSaturday
    };

    function getWeeksString(date1, date2) {
        const oneweek = 604800000; //ms in one week
        const weeks = Math.ceil((new Date(date2).setHours(0, 0, 0, 0).valueOf() - new Date(date1).setHours(0, 0, 0, 0).valueOf()) / oneweek);
        return `${weeks.toString()} ${(weeks > 1) ? resourceService.get('WEEKS') : resourceService.get('WEEK')}`;
    }

    function DateRange(start, end, timeSupport = false) {
        this.start = serialiseDate(start);
        this.end = serialiseDate(end);
        this.timeSupport = timeSupport;
        this.isStartTimeApprovedByCustomer = timeSupport ? false : undefined;
        this.isEndTimeApprovedByCustomer = timeSupport ? false : undefined;
    }

    DateRange.prototype.setStart = function(val, defaultTimeObj) {
        this.start = serialiseDate(val);
        if ((this.end == null && this.start != null) ||
            (this.end != null && this.start != null && numberOfNights(this.start, this.end) < 1)) {
            const areaInfo = getAreaInfo();
            const endDateOffset = areaInfo && areaInfo.endDateOffset || 1;
            const endDate = addDays(this.start, endDateOffset, true);

            if (this.timeSupport) {
                this.end = defaultTime(endDate, true, defaultTimeObj);
            } else {
                this.end = endDate;
            }
        }
        this.durationSummary = null;
        this.nights = null;
    };

    DateRange.prototype.setEnd = function(val) {
        this.end = serialiseDate(val);
        this.durationSummary = null;
        this.nights = null;
    };

    DateRange.prototype.getDurationSummary = function() {
        if (this.durationSummary) {
            return this.durationSummary;
        }
        this.durationSummary = getDurationSummary(this.start, this.end);
        return this.durationSummary;
    };

    DateRange.prototype.numberOfNights = function() {
        if (this.nights) {
            return this.nights;
        }
        this.nights = numberOfNights(this.start, this.end);
        return this.nights;
    };

    function today() {
        return zeroTime(new Date());
    }

    function zeroTime(date, serialised) {
        if (!date) { return null; }
        date = parseDate(date);
        const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        d.setMilliseconds(0);
        return _serialiseIfNecessary(d, serialised);
    }

    function defaultTime(date, serialised, defaultTimeObj) {
        if (!date) { return null; }
        date = parseDate(date);
        const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        if (defaultTimeObj) {
            d.setHours(defaultTimeObj.hours, defaultTimeObj.minutes);
        } else {
            d.setHours(11, 30);
        }
        return _serialiseIfNecessary(d, serialised);
    }

    function _toUtc(date, serialised) {
        if (!date) { return null; }
        date = parseDate(date);
        return _serialiseIfNecessary(new Date(date.getTime() - (date.getTimezoneOffset() * 60000)), serialised);
    }

    function todayPlus(options, ...rest) {
        if (!_.isObject(options)) {
            options = {
                days: options,
                hours: rest[0],
                serialised: rest[1]
            };
        }
        const d = zeroTime(new Date());
        if (options.months) {
            d.setMonth(d.getMonth() + options.months);
        }
        if (options.days) {
            d.setDate(d.getDate() + options.days);
        }
        if (options.hours) {
            d.setHours(options.hours);
        }
        return _serialiseIfNecessary(d, options.serialised);
    }

    function numberOfNights(earliest, latest) {
        if (earliest && latest) {
            const [e, l] = alignDates([earliest, latest], true);
            return Math.ceil((l.getTime() - e.getTime()) / oneDay);
        }
        return -1;
    }

    function format(date, fmt) {
        if (!date) {
            return null;
        }
        date = parseDate(date);
        return $filter('unadjustedDate')(date, fmt || 'd MMM yyyy');
    }

    function atLeastOneDayInTheFuture(date) {
        return _xDaysInTheFuture(date, 1);
    }

    function atLeastTwoDaysInTheFuture(date) {
        return _xDaysInTheFuture(date, 2);
    }

    function addDays(date, days, serialised) {
        if (!date) { return null; }
        date = parseDate(date);
        const d = new Date(date);
        d.setDate(date.getDate() + days);
        return _serialiseIfNecessary(d, serialised);
    }

    function addMinutes(date, minutes, serialised) {
        if (!date) { return null; }
        date = parseDate(date);
        const d = new Date(date);
        d.setMinutes(d.getMinutes() + minutes);
        return _serialiseIfNecessary(d, serialised);
    }

    function addHours(date, hours, serialised) {
        if (!date) { return null; }
        date = parseDate(date);
        const d = new Date(date);
        d.setHours(d.getHours() + hours);
        return _serialiseIfNecessary(d, serialised);
    }

    function getAge(birthDate, returnDate) {
        if (!birthDate || !returnDate) { return null; }
        birthDate = parseDate(birthDate);
        returnDate = parseDate(returnDate);
        let age = returnDate.getFullYear() - birthDate.getFullYear();
        const m = returnDate.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && returnDate.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    }

    function getDurationSummary(from, to) {
        from = parseDate(from);
        to = parseDate(to);
        if (!from || !isValid(from)) {
            return '';
        }

        if (to == null) {    //perhaps for a one way flight
            return format(from);
        }
        if (to.getTime() === 0) {
            return format(from);
        }
        if (!isValid(to)) {
            return '';
        }
        const days = numberOfNights(from, to);
        return `${days} ${
            days > 1 ? resourceService.get('NIGHTS') : resourceService.get('NIGHT')
            }, (${format(from)}) - (${format(to)})`;
    }

    function minutesToHoursAndMinutes(m, leadingZero = true) {
        const hours = leadingZero ? $filter('frontPad')(Math.floor(m / 60)) : Math.floor(m / 60);
        const minutes = $filter('frontPad')(m % 60);
        return `${hours + resourceService.get('HOURABBR')} ${minutes}${resourceService.get('MINUTEABBR')}`;
    }

    function isValid(d) {
        d = parseDate(d);
        return d instanceof Date && isFinite(d);
    }

    //this function will treat all datetime strings as if they are GMT
    //if there is no timezone we add 'Z', if there is any other timezone
    //we replace with 'Z'. This allows us to then parse the date and
    //then undo the browser's adjustment meaning that we end up with whatever
    //we started with, regardless of timezone.
    function parseDate(str) {
        if (!str) { return null; }

        if (str instanceof Date) {
            return str;
        }

        if (typeof str === 'string' && /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(str)) {
            str += 'T00:00:00Z';
        }

        //we are expecting an ISO string but we need to check if it ends with a timezone
        //if it doesn't we must add Z for GMT because firefox does not assume Z as it should
        //according to http://www.ecma-international.org/ecma-262/5.1/#sec-15.9.1.15
        if (typeof str === 'string' && !/^.+((\+|-)[0-9]{2}:[0-9]{2}|Z)$/.test(str)) {
            str += 'Z';
        }

        //if the date string contains a timezone string other than 'Z' we need to replace it with 'Z'
        if (typeof str === 'string') {
            str = str.replace(/^(.+)((\+|-)[0-9]{2}:[0-9]{2})$/, function(m, p1) {
                return `${p1}Z`;
            });
        }

        //parse the date into a new local timezone date object
        const d = new Date(str);

        //undo the offset that the browser *may* have applied so that we end up with
        //a date time that looks like the original string - yucky
        return new Date(d.getTime() + (d.getTimezoneOffset() * 60000));
    }

    function sameDay(d1, d2) {
        if (!d1 || !d2) { return null; }
        return zeroTime(d1, false).getTime() === zeroTime(d2, false).getTime();
    }

    function sameMonth(d1, d2) {
        if (!d1 || !d2) {
            return null;
        }
        return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth();
    }

    function roundedToStartOfHour(date, serialised) {
        if (!date) { return null; }
        date = parseDate(date);
        const newDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), 0, 0);
        const minutes = date.getMinutes();

        if (minutes < 15) {
            return _serialiseIfNecessary(newDate, serialised);
        }
        if (minutes < 45) {
            return service.addMinutes(newDate, 30, serialised);
        }
        return service.addHours(newDate, 1, serialised);
    }

    function dayDiff(date1, date2) {
        if (!date1 || !date2) { return null; }
        date1 = parseDate(date1);
        date2 = parseDate(date2);
        const [a, b] = alignDates([date1, date2], true);
        return Math.abs(Math.floor((a - b) / oneDay));
    }

    function thisWeek() {
        const t = today();
        const y = t.getFullYear();
        const m = t.getMonth();
        const d = t.getDate();
        const dow = t.getDay();
        const startDay = 1; // monday
        const startDate = (d - dow) + startDay;
        const start = zeroTime(new Date(y, m, startDate));
        const end = addDays(start, 6);
        return new DateRange(start, end);
    }

    function nextWeek() {
        const dateRange = thisWeek();
        return new DateRange(addDays(dateRange.start, 7), addDays(dateRange.end, 7));
    }

    function thisMonth() {
        const d = today();
        const y = d.getFullYear();
        const m = d.getMonth();
        const start = zeroTime(new Date(y, m, 1));
        const end = zeroTime(new Date(y, m + 1, 0));
        return new DateRange(start, end);
    }

    function specificMonth(month, year) {
        const start = zeroTime(new Date(year, month, 1));
        const end = zeroTime(new Date(year, month + 1, 0));
        return new DateRange(start, end);
    }

    function serialiseDate(date) {
        date = parseDate(date);
        if (isValid(date)) {
            return _toUtc(date).toISOString();
        }
        return null;
    }

    function _serialiseIfNecessary(d, serialised) {
        return (serialised ? serialiseDate : parseDate)(d);
    }

    function _xDaysInTheFuture(date, days) {
        if (!date) { return null; }
        let t = today();
        [date, t] = alignDates([date, t], true);
        return date.getTime() - t.getTime() >= (oneDay * days);
    }

    // to be used exclusively for dates comparisons && calculations
    function alignDates(dates, zeroTime = false) {
        return dates.map(_.flowRight(_toUtc, zeroTime ? service.zeroTime : d => d));
    }


    /*
        returns -1 when d1<d2,
        returns 1 when d1>d2,
        return 0 when d1=d2
    */
    function compareDates(d1, d2) {
        const [a1, a2] = alignDates([d1, d2]);
        if (a1 < a2) {
            return -1;
        }
        if (a1 > a2) {
            return 1;
        }
        return 0;
    }

    function totalMinutes(d1, d2) {
        const start = parseDate(d1);
        const end = parseDate(d2);
        const diffMs = end - start; // milliseconds between start & end

        return Math.floor(diffMs / 60000);
    }

    function getDifferenceInDays(d1, d2) {
        if (!d1 || !d2) { return 0; }
        const startTime = angular.copy(d1).setHours(0, 0, 0, 0);
        const endTime = angular.copy(d2).setHours(0, 0, 0, 0);
        return Math.floor((endTime - startTime) / (1000 * 60 * 60 * 24));
    }

    function getDateInterval(date, rangeLength, skipPivot) {
        const minimumDate = todayPlus(1, 0, false);
        const halfRange = Math.floor(rangeLength / 2);

        const initialDate = skipPivot ? date : addDays(date, -halfRange);
        const comparison = compareDates(initialDate, minimumDate);
        const startingDate = zeroTime(comparison === -1 ? minimumDate : initialDate);

        const dates = [];
        for (let i = 0, ii = rangeLength; i < ii; i++) {
            const d = addDays(startingDate, i).setHours(0, 0, 0, 0);
            dates.push(d);
        }
        return dates;
    }

    function getNextSaturday(weeks) {
        // This function finds your weekend in the future.
        const theFuture = todayPlus(weeks * 7);
        const howMaynDaysYet = 6 - theFuture.getDay();
        return addDays(theFuture, howMaynDaysYet, true);
    }

    let _Area;
    function getAreaInfo() {
        if (!_Area) {
            //I know $injector is not awesome but I'm trying to limit how much I refactor.
            _Area = $injector.get('Area');
        }
        const area = $route.current && $route.current.$$route && $route.current.$$route.area;
        return _Area[area];
    }

    function checkZonedDateFromat(str) {
        // Time-zoned date strings must have a four digit time zone in order to be parsed successfully by new Date()
        // valid formats: 'YYYY-MM-DDThh:mm:ss+hhmm' or 'YYYY-MM-DDThh:mm:ss+hh:mm'
        // eg: '2016-08-07T15:05:00+0100' or '2016-08-07T15:05:00+01:00'

        if (str === null) {
            return;
        }

        const time = str.substring(str.indexOf('T') + 1);

        let pos = time.indexOf('+');
        if (pos === -1) {
            pos = time.indexOf('-');
        }

        if (time.substring(pos + 1).length === 2) {
            // the time zone only specifies hours so we need to add minutes
            str += ':00';
        }

        return (new Date(str)).toISOString();
    }

    return service;
}
})();
